import { useState } from 'react';
import { Plus, Pencil } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable, type DataTableColumn } from '../../components/TwDataTable';
import {
  PageHeader, Card, Button, IconButton, Modal, Form, useForm, Input, InputNumber, DatePicker, Row, Col, Tag, message,
} from '../../ui';

export function TaxMastersPage() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const canManage = usePermission('TAX_MASTER.ADMIN'); // create/edit needs ADMIN; VIEW is read-only

  const { data, isLoading } = useQuery({
    queryKey: ['tax-masters-all'],
    queryFn: () => request<any[]>({ url: '/tax-masters' }),
  });

  const columns: DataTableColumn<any>[] = [
    { key: 'hsnCode', title: 'HSN', dataIndex: 'hsnCode', sortable: true, alwaysVisible: true },
    { key: 'description', title: 'Description', dataIndex: 'description', sortable: true, alwaysVisible: true },
    { key: 'cgstPct', title: 'CGST %', dataIndex: 'cgstPct', sortable: true, align: 'right', render: (v: any) => `${v}%` },
    { key: 'sgstPct', title: 'SGST %', dataIndex: 'sgstPct', sortable: true, align: 'right', render: (v: any) => `${v}%` },
    { key: 'igstPct', title: 'IGST %', dataIndex: 'igstPct', sortable: true, align: 'right', render: (v: any) => <b>{v}%</b> },
    { key: 'effectiveFrom', title: 'From', dataIndex: 'effectiveFrom', sortable: true },
    { key: 'effectiveTo', title: 'To', dataIndex: 'effectiveTo', render: (v: string) => v ?? <Tag tone="green">Current</Tag> },
    ...(canManage ? [{
      key: 'actions', title: '', alwaysVisible: true, width: 70,
      render: (_: any, r: any) => (
        <IconButton diameter={32} aria-label="Edit HSN rate" className="border border-line text-muted hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
          onClick={(e) => { e.stopPropagation(); setEditing(r); setOpen(true); }}>
          <Pencil size={16} />
        </IconButton>
      ),
    } as DataTableColumn<any>] : []),
  ];

  return (
    <div>
      <PageHeader
        title="Tax masters"
        subtitle="HSN codes and their GST rates by effective period."
        actions={
          canManage ? (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditing(null); setOpen(true); }}>
              Add HSN rate
            </Button>
          ) : undefined
        }
      />
      <Card bodyClassName="p-0 sm:p-0">
        <div className="p-4">
          <TwDataTable
            storageKey="tax-masters"
            rowKey="id"
            loading={isLoading}
            dataSource={data ?? []}
            columns={columns}
            onRow={(r) => ({ onClick: canManage ? () => { setEditing(r); setOpen(true); } : undefined })}
            pagination={{ pageSize: 25, showSizeChanger: true }}
          />
        </div>
      </Card>

      <TaxFormModal open={open} record={editing} onClose={() => { setOpen(false); setEditing(null); }} />
    </div>
  );
}

function TaxFormModal({ open, onClose, record }: { open: boolean; onClose: () => void; record?: any }) {
  const [form] = useForm();
  const qc = useQueryClient();
  const isEdit = !!record;

  // Seed via initialValues + a per-target key (below) so the form mounts fresh with the right values
  // every open — avoids the setFieldsValue-in-effect race against the Radix-modal remount (blank fields
  // and a prior HSN's values leaking in → duplicate-key on save).
  const initialValues = record
    ? {
        ...record,
        effectiveFrom: record.effectiveFrom ? dayjs(record.effectiveFrom) : null,
        effectiveTo: record.effectiveTo ? dayjs(record.effectiveTo) : null,
        cgstPct: Number(record.cgstPct ?? 0),
        sgstPct: Number(record.sgstPct ?? 0),
        igstPct: Number(record.igstPct ?? 0),
      }
    : { effectiveFrom: dayjs() };

  const save = useMutation({
    mutationFn: (vals: any) => {
      const payload = {
        ...vals,
        effectiveFrom: vals.effectiveFrom?.format('YYYY-MM-DD'),
        effectiveTo: vals.effectiveTo?.format('YYYY-MM-DD') ?? null,
        cgstPct: String(vals.cgstPct ?? 0),
        sgstPct: String(vals.sgstPct ?? 0),
        igstPct: String(vals.igstPct ?? 0),
      };
      return isEdit
        ? request({ url: `/tax-masters/${record.id}`, method: 'PUT', data: payload })
        : request({ url: '/tax-masters', method: 'POST', data: payload });
    },
    onSuccess: () => {
      message.success(isEdit ? 'Updated' : 'Created');
      qc.invalidateQueries({ queryKey: ['tax-masters-all'] });
      qc.invalidateQueries({ queryKey: ['tax-masters'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isEdit ? `Edit · HSN ${record?.hsnCode}` : 'New tax rate'}
      width={640}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>{isEdit ? 'Update' : 'Create'}</Button>
        </>
      }
    >
      <Form key={record?.id ?? 'new'} form={form} layout="vertical" initialValues={initialValues} onFinish={(v) => save.mutate(v)}>
        <Row gutter={16}>
          <Col span={10}>
            <Form.Item name="hsnCode" label="HSN code"
              rules={[{ required: true }, { pattern: /^\d{4}(\d{2}(\d{2})?)?$/, message: 'HSN must be 4, 6 or 8 digits' }]}>
              <Input placeholder="38249990" maxLength={10} />
            </Form.Item>
          </Col>
          <Col span={14}>
            <Form.Item name="description" label="Description" rules={[{ required: true }]}>
              <Input placeholder="Bio-stimulant other" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="cgstPct" label="CGST %" rules={[{ required: true }]}>
              <InputNumber className="w-full" min={0} max={50} step={0.5} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="sgstPct" label="SGST %" rules={[{ required: true }]}>
              <InputNumber className="w-full" min={0} max={50} step={0.5} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="igstPct"
              label="IGST %"
              dependencies={['cgstPct', 'sgstPct']}
              rules={[
                { required: true },
                (f: any) => ({
                  warningOnly: true, // usually IGST = CGST + SGST, but allow rare overrides (cess etc.)
                  validator: (_: unknown, v: any) => {
                    const c = Number(f.getFieldValue('cgstPct') ?? 0);
                    const s = Number(f.getFieldValue('sgstPct') ?? 0);
                    if (v != null && Math.abs(Number(v) - (c + s)) > 0.001) {
                      return Promise.reject(new Error(`Usually IGST = CGST + SGST (${c + s}%)`));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber className="w-full" min={0} max={50} step={0.5} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="effectiveFrom" label="Effective from" rules={[{ required: true }]}>
              <DatePicker className="w-full" format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="effectiveTo"
              label="Effective to (blank = current)"
              dependencies={['effectiveFrom']}
              rules={[
                (f: any) => ({
                  validator: (_: unknown, v: any) => {
                    const from = f.getFieldValue('effectiveFrom');
                    if (v && from && v.isBefore(from, 'day')) {
                      return Promise.reject(new Error('Must be on or after "Effective from"'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker className="w-full" format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
